// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// CIPC Registration: 2019/443098/07
// Author: Velly Sipho Phaloane
//
// This source code is the proprietary and confidential property of
// Motswagae Holding (Pty) Ltd, trading as 360Wallet.
// Unauthorized copying, modification, distribution, or use of this
// software, in whole or in part, is strictly prohibited.
//
// Protected under the Copyright Act 98 of 1978 (South Africa)
// and international copyright treaties.
import { useState, useEffect } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import { getAuditLogs } from "../lib/api";
import { toast } from "sonner";
import { ScrollText, Search, ChevronDown, ChevronRight } from "lucide-react";
import { useState as useLocalState } from "react";

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const eventTypeColors = {
  USER_CREATED: "text-green-400",
  USER_LOGIN: "text-blue-400",
  BANK_CREATED: "text-green-400",
  BANK_UPDATED: "text-yellow-400",
  BANK_DEACTIVATED: "text-red-400",
  AUTHORIZATION_CREATED: "text-green-400",
  AUTHORIZATION_CANCELLED: "text-yellow-400",
  AUTHORIZATION_BLOCKED: "text-red-400",
  ATM_WITHDRAWAL_FAILED: "text-red-400",
  ATM_PIN_FAILED: "text-yellow-400",
  ATM_DISPENSE_SUCCESS: "text-green-400",
  PRICING_CREATED: "text-green-400",
  PRICING_UPDATED: "text-yellow-400",
  SETTLEMENT_GENERATED: "text-blue-400",
  SETTLEMENT_STATUS_UPDATED: "text-yellow-400",
  SETTLEMENT_EXPORTED: "text-blue-400"
};

const ExpandableRow = ({ log }) => {
  const [expanded, setExpanded] = useLocalState(false);

  return (
    <>
      <TableRow 
        className="cursor-pointer hover:bg-secondary/50"
        onClick={() => setExpanded(!expanded)}
      >
        <TableCell>
          <span className="font-mono text-xs text-muted-foreground">
            {formatDate(log.timestamp)}
          </span>
        </TableCell>
        <TableCell>
          <span className={`font-mono text-sm ${eventTypeColors[log.event_type] || 'text-foreground'}`}>
            {log.event_type}
          </span>
        </TableCell>
        <TableCell>
          <code className="text-xs bg-secondary px-2 py-1 rounded">
            {log.entity_type}
          </code>
        </TableCell>
        <TableCell>
          <code className="font-mono text-xs text-muted-foreground">
            {log.entity_id.slice(0, 8)}...
          </code>
        </TableCell>
        <TableCell>
          <span className="text-sm text-muted-foreground">
            {log.actor_email || "System"}
          </span>
        </TableCell>
        <TableCell>
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={6} className="bg-secondary/30">
            <div className="p-4">
              <p className="text-xs text-muted-foreground mb-2">Event Details:</p>
              <ScrollArea className="max-h-40">
                <pre className="font-mono text-xs bg-background p-3 rounded overflow-auto">
                  {JSON.stringify(log.details, null, 2)}
                </pre>
              </ScrollArea>
              {log.ip_address && (
                <p className="text-xs text-muted-foreground mt-2">
                  IP: {log.ip_address}
                </p>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterEventType, setFilterEventType] = useState("all");
  const [filterEntityType, setFilterEntityType] = useState("all");

  const fetchLogs = async () => {
    try {
      const params = { limit: 200 };
      if (filterEventType !== "all") params.event_type = filterEventType;
      if (filterEntityType !== "all") params.entity_type = filterEntityType;
      
      const res = await getAuditLogs(params);
      setLogs(res.data);
    } catch (error) {
      toast.error("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [filterEventType, filterEntityType]);

  const filteredLogs = logs.filter(log =>
    log.event_type.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.entity_type.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.entity_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (log.actor_email && log.actor_email.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Get unique values for filters
  const eventTypes = [...new Set(logs.map(l => l.event_type))].sort();
  const entityTypes = [...new Set(logs.map(l => l.entity_type))].sort();

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl">Audit Logs</h1>
          <p className="text-muted-foreground">Immutable system event history</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search logs..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-logs-input"
              />
            </div>
            <Select value={filterEventType} onValueChange={setFilterEventType}>
              <SelectTrigger data-testid="filter-event-type">
                <SelectValue placeholder="All Events" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                {eventTypes.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterEntityType} onValueChange={setFilterEntityType}>
              <SelectTrigger data-testid="filter-entity-type">
                <SelectValue placeholder="All Entities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                {entityTypes.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Entity ID</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="animate-pulse text-muted-foreground">Loading audit logs...</div>
                  </TableCell>
                </TableRow>
              ) : filteredLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2">
                      <ScrollText className="h-8 w-8 text-muted-foreground" />
                      <p className="text-muted-foreground">No audit logs found</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredLogs.map((log) => (
                  <ExpandableRow key={log.id} log={log} />
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Info */}
      <Card className="bg-secondary/30 border-border">
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground">
            <strong>Note:</strong> Audit logs are immutable and cannot be modified or deleted. 
            All system events are automatically logged for compliance and security purposes.
            Click on a row to expand and view full event details.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogs;
